import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
  ActivityIndicator,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { FontFamily } from "../../../../../constants/fonts";
import {
  LoadExecutionScorecardsData,
  LoadExecutionDetailRecord,
  AppliedFiltersForLoadExecution,
  fetchLoadExecutionSsccBreakdown,
} from "../services/onTimeDispatchLoadExecutionApi";
import DLOTooltip from "../../../../../modules/dlo/components/DLOTooltip";
import { useLoadExecutionExport } from "../hooks/useLoadExecutionExport";
import { useToast } from "../../../../../components/LhToastContainer";
import LoadExecutionScorecardSkeleton from "./LoadExecutionScorecardSkeleton";
import LoadExecutionMobileCards from "./LoadExecutionMobileCards";
import { useTheme } from "../../../../../theme/useTheme";

export interface LoadExecutionSsccDetail {
  stop: number;
  ssccId: string;
  status: string;
  eta: string;
}

const stableHash = (input: string): number => {
  let hash = 0;
  for (let idx = 0; idx < input.length; idx++) {
    hash = (hash * 31 + input.charCodeAt(idx)) >>> 0;
  }
  return hash;
};

const deterministicBetween = (
  key: string,
  min: number,
  max: number
): number => {
  const h = stableHash(key) % 10000;
  const ratio = h / 10000;
  return min + (max - min) * ratio;
};

export function generateDeterministicSsccDetails(
  loadId: string,
  manifestHour: number = 3
): LoadExecutionSsccDetail[] {
  const ssccCount = Math.max(
    1,
    Math.round(deterministicBetween(`${loadId}-sscc`, 1, 3))
  );

  return Array.from({ length: ssccCount }, (_, ssccIndex) => {
    const ssccStatus =
      deterministicBetween(
        `${loadId}-sscc-${ssccIndex}-status`,
        0,
        1
      ) > 0.35
        ? "Not Loaded"
        : "Picking";

    const barcodeNum = Math.round(
      deterministicBetween(
        `${loadId}-sscc-${ssccIndex}-barcode`,
        19280000,
        19289999
      )
    );
    const stopNum =
      ssccIndex === 0
        ? Math.round(deterministicBetween(`${loadId}-sscc-stop-1`, 1, 3))
        : (ssccIndex + 1) * 2 + 1;

    const baseHour = Number.isFinite(manifestHour) && manifestHour > 0 ? manifestHour : 3;
    const etaHour = Math.max(0, Math.min(23, baseHour + 4));
    const etaMin = String(10 + ssccIndex * 15).padStart(2, "0");

    return {
      stop: stopNum,
      ssccId: `(00) 1 0614141 ${barcodeNum} ${ssccIndex + 1}`,
      status: ssccStatus,
      eta: `${String(etaHour).padStart(2, "0")}:${etaMin}`,
    };
  });
}

const resolveVehicleId = (rawVehicle: any, id: string): string => {
  if (
    typeof rawVehicle === "string" &&
    rawVehicle.trim() !== "" &&
    rawVehicle.trim() !== "—" &&
    rawVehicle.trim() !== "-"
  ) {
    return rawVehicle.trim();
  }
  return `PV-${Math.round(deterministicBetween(`${id}-vehicle`, 20000, 24999))}`;
};

const resolveOrderCount = (rawCount: any, id: string): number => {
  const num = Number(rawCount);
  if (Number.isFinite(num) && num > 0) {
    return num;
  }
  return Math.round(deterministicBetween(`${id}-orders`, 14, 38));
};

export interface LoadExecutionRow {
  id: string;
  region: string;
  market: string;
  location: string;
  locationName?: string | null; // ✅ FIX: Add locationName field to store display name
  locationType: string;
  driver: string;
  status: "Complete" | "Incomplete";
  onTime: boolean;
  arrivedAsExpected: "Y" | "N" | "Blank";
  manifestHour: number;
  deliveryDate: string;
  localRouteId: string;
  sequence?: string | null;
  nationalRouteId: string;
  manifestArrivalDateTime: string;
  actualArrivalDateTime: string;
  actualCompletedDateTime: string;
  highlightCompletionTime?: boolean;
  completionTimeTooltip?: string | null;
  completionTimeHighlightColor?: string | null;
  palletCount?: number | null;
  percentComplete: number;
  vehicle: string;
  orderCount: number;
  ssccDetails: LoadExecutionSsccDetail[];
}

type SortKey =
  | "id"
  | "deliveryDate"
  | "location"
  | "localRouteId"
  | "nationalRouteId"
  | "driver"
  | "manifestArrivalDateTime"
  | "actualArrivalDateTime"
  | "arrivedAsExpected"
  | "actualCompletedDateTime"
  | "palletCount"
  | "percentComplete"
  | "onTime"
  | "status";

interface LoadExecutionProps {
  rows: LoadExecutionRow[];
  detailRecords?: LoadExecutionDetailRecord[];
  detailLoading?: boolean;
  detailError?: string | null;
  totalElements?: number;
  pageNumber?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  onSort?: (sortBy: string, sortDirection: 'ASC' | 'DESC' | null) => void;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  accessibleLocationIds?: string[];
  scorecardData?: LoadExecutionScorecardsData | null;
  scorecardLoading?: boolean;
  scorecardError?: string | null;
  // Export props
  appliedFilters?: AppliedFiltersForLoadExecution;
  adminLocations?: any[];
  /**
   * @deprecated No longer used to derive Dark mode — `isDark` is now
   * derived directly from the global theme's `resolvedTheme` so every
   * platform (web, iPad, mobile phone) stays in sync. Kept optional for
   * backward compatibility with existing call sites.
   */
  isDarkTablet?: boolean;
}

/**
 * LoadExecutionTableSkeleton Component
 * Displays animated skeleton loading placeholders for table rows during API calls
 * Maintains table structure while data is being fetched
 */
const LoadExecutionTableSkeleton: React.FC<{ rowCount?: number; isDark?: boolean }> = ({ rowCount = 8, isDark = false }) => {
  const pulse = useRef(new Animated.Value(0.45)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 800,
          useNativeDriver: false,
        }),
        Animated.timing(pulse, {
          toValue: 0.45,
          duration: 800,
          useNativeDriver: false,
        }),
      ])
    ).start();
  }, [pulse]);

  const skeletonBg = isDark ? '#2B2B2B' : '#E5E7EB';

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator>
      <View style={{ minWidth: LOAD_EXECUTION_TABLE_MIN_WIDTH }}>
        {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <View
          key={`skeleton-row-${rowIndex}`}
          style={[
            styles.bodyRow,
            isDark && { backgroundColor: '#1E1E1E', borderBottomColor: '#303030' },
            rowIndex % 2 === 1 && styles.bodyRowAlt,
            rowIndex % 2 === 1 && isDark && { backgroundColor: '#242424' },
          ]}
        >
          {/* Expand icon cell */}
          <View style={[styles.cell, styles.leftCell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.expand }]}>
            <Animated.View
              style={[
                { height: 16, width: 16, borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Dispatch ID cell */}
          <View style={[styles.cell, styles.leftCell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.id }]}>
            <Animated.View
              style={[
                { height: 14, width: '70%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Delivery Date cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.deliveryDate }]}>
            <Animated.View
              style={[
                { height: 14, width: '60%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Location cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.location }]}>
            <Animated.View
              style={[
                { height: 14, width: '75%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Local Route ID cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.localRouteId }]}>
            <Animated.View
              style={[
                { height: 14, width: '65%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* National Route ID cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.nationalRouteId }]}>
            <Animated.View
              style={[
                { height: 14, width: '55%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Driver cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.driver }]}>
            <Animated.View
              style={[
                { height: 14, width: '70%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Manifest Arrival Date/Time cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.manifestArrivalDateTime }]}>
            <Animated.View
              style={[
                { height: 14, width: '80%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Actual Arrival Date/Time cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualArrivalDateTime }]}>
            <Animated.View
              style={[
                { height: 14, width: '80%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Arrived as Expected cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.arrivedAsExpected }]}>
            <Animated.View
              style={[
                { height: 14, width: 30, borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Actual Completed Date/Time cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualCompletedDateTime }]}>
            <Animated.View
              style={[
                { height: 14, width: '75%', borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Percent Complete cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.percentComplete }]}>
            <Animated.View
              style={[
                { height: 14, width: 40, borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* On-Time cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.onTime }]}>
            <Animated.View
              style={[
                { height: 14, width: 50, borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>

          {/* Status cell */}
          <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.status, paddingRight: 2 }]}>
            <Animated.View
              style={[
                { height: 14, width: 60, borderRadius: 4, opacity: pulse },
                { backgroundColor: skeletonBg },
              ]}
            />
          </View>
        </View>
        ))}
      </View>
    </ScrollView>
  );
};


/**
 * Helper function to filter loads based on user's location access
 * MOCK DATA MODE: Currently returns all rows (no filtering)
 * 
 * TODO: BACKEND INTEGRATION
 * Replace this function with actual location-access API call when endpoint is ready.
 * Current implementation is placeholder for future backend integration.
 * 
 * @param rows - All load execution rows from data layer
 * @param accessibleLocationIds - User's accessible locations (TODO: from backend)
 * @returns Filtered rows (currently all rows in mock mode)
 */
const gateLoadsByLocationAccess = (
  rows: LoadExecutionRow[],
  accessibleLocationIds?: string[]
): LoadExecutionRow[] => {
  // MOCK DATA: No filtering - show all loads
  // Backend integration will add logic:
  // if (!accessibleLocationIds?.length) return [];
  // return rows.filter(row => accessibleLocationIds.includes(row.location));
  return rows;
}

// Fixed pixel widths (replacing flex ratios) so the table can overflow and scroll horizontally.
const LOAD_EXECUTION_COLUMN_WIDTHS = {
  expand: 40,
  id: 110,
  deliveryDate: 100,
  location: 130,
  localRouteId: 140,
  nationalRouteId: 110,
  driver: 110,
  manifestArrivalDateTime: 170,
  actualArrivalDateTime: 160,
  arrivedAsExpected: 110,
  actualCompletedDateTime: 220,
  palletCount: 100,
  percentComplete: 110,
  onTime: 90,
  status: 90,
} as const;
const LOAD_EXECUTION_TABLE_MIN_WIDTH = Object.values(
  LOAD_EXECUTION_COLUMN_WIDTHS
).reduce((sum, w) => sum + w, 0);

// The Expand + Dispatch ID columns are pulled out of the horizontally
// scrolling area on iPad/mobile breakpoints so they can act as a genuine
// sticky-left column (see `useStickyIdColumn` below for why this can't be
// done with a `position: 'sticky'` style on native platforms).
const LOAD_EXECUTION_STICKY_LEFT_WIDTH =
  LOAD_EXECUTION_COLUMN_WIDTHS.expand + LOAD_EXECUTION_COLUMN_WIDTHS.id;
const LOAD_EXECUTION_SCROLLABLE_MIN_WIDTH =
  LOAD_EXECUTION_TABLE_MIN_WIDTH - LOAD_EXECUTION_STICKY_LEFT_WIDTH;
// Web viewport width below which the table switches to a split sticky-left-
// column layout. Native (iPad) always uses it: landscape widths are 1024-1366,
// so a width threshold alone would skip it.
const STICKY_ID_COLUMN_WEB_MAX_WIDTH = 1024;

const formatHourLabel = (hour: number): string =>
  `${String(hour).padStart(2, "0")}:00`;

// Backend sends "Yes"/"No" (not "Y"/"N"); normalize to the table's Y/N/Blank tag values.
const normalizeArrivedAsExpected = (value: string | null | undefined): "Y" | "N" | "Blank" => {
  switch (String(value ?? "").trim().toLowerCase()) {
    case "yes":
    case "y":
      return "Y";
    case "no":
    case "n":
      return "N";
    default:
      return "Blank";
  }
};

const formatLocalRouteWithSequence = (
  localRouteId: string,
  sequence?: string | null
): string => {
  const route = String(localRouteId ?? "").trim();
  const seq = String(sequence ?? "").trim();
  return seq ? `${route} + ${seq}` : route;
};

const csvEscape = (value: unknown): string => {
  const str = String(value ?? "");
  if (!/[",\n]/.test(str)) return str;
  return `"${str.replace(/"/g, '""')}"`;
};

const LoadExecution: React.FC<LoadExecutionProps> = ({
  rows,
  detailRecords,
  detailLoading = false,
  detailError,
  totalElements = 0,
  pageNumber = 1,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  onSort: onSortProp,
  sortBy: apiSortBy,
  sortDirection: apiSortDirection,
  accessibleLocationIds,
  scorecardData,
  scorecardLoading = false,
  scorecardError,
  appliedFilters,
  adminLocations,
  // isDarkTablet intentionally not destructured — see @deprecated note above.
}) => {
  const { width } = useWindowDimensions();
  const { resolvedTheme, colors } = useTheme();
  // ✅ Mobile detection: iOS/native with width < 768 = mobile phone, not
  // tablet. This stays Platform-gated on purpose — it drives which LAYOUT
  // (card list vs. table) renders, not theming.
  const isMobilePhone = Platform.OS === 'ios' && width < 768;
  // Derived directly from the global theme (not gated behind
  // Platform.OS === 'ios' / isDarkTablet) so Dark mode is applied
  // consistently on every platform — web, iPad, and native mobile phone —
  // and updates immediately when the user switches theme. The previous
  // `isDarkPhone || isDarkTablet` gate left web (and Android) permanently
  // in Light-mode styling regardless of the actual resolved theme, which
  // was part of why the selected "Incomplete Loads" card's Dark mode
  // colors never appeared on web.
  const isDark = resolvedTheme === 'dark';
  const summaryTooltipTrigger = Platform.OS === 'web' ? 'hover' : 'click';
  // Mobile card view still needs to know Dark mode too.
  const isDarkPhone = isDark;
  // ✅ Sticky Dispatch ID column (iPad & mobile breakpoints only):
  // React Native has no native equivalent of CSS `position: sticky` for
  // horizontal scrolling — ScrollView only supports `stickyHeaderIndices`,
  // which is vertical-only. A `position: 'sticky'` *style* only works on
  // react-native-web (it forwards straight to the DOM); it is silently
  // ignored on native iOS/Android. So on native, the only way to get a
  // truly fixed left column during horizontal scroll is to physically
  // render it OUTSIDE the horizontal ScrollView. `useStickyIdColumn` below
  // switches the table to that split layout (fixed Expand+Dispatch ID
  // column alongside a horizontally-scrolling ScrollView for the rest),
  // and it works identically on web, iPad (iOS), and Android — not just web.
  const useStickyIdColumn = Platform.OS !== "web" || width < STICKY_ID_COLUMN_WEB_MAX_WIDTH;
  // Measured height (in px) of each expanded row's SSCC panel, keyed by
  // rowKey. The panel only renders in the horizontally-scrolling right
  // side of the split layout; this lets the fixed left column render a
  // same-height blank spacer underneath its Dispatch ID cell so rows stay
  // vertically aligned between the two independently-rendered halves.
  const [expandedRowHeights, setExpandedRowHeights] = useState<Record<string, number>>({});
  const [sortKey, setSortKey] = useState<SortKey>("manifestArrivalDateTime");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [fetchedSsccBreakdowns, setFetchedSsccBreakdowns] = useState<Record<string, LoadExecutionSsccDetail[]>>({});
  const [fetchingSsccMap, setFetchingSsccMap] = useState<Record<string, boolean>>({});
  // Per-dispatchId error message from the sscc-breakdown API. Presence of a
  // key here (vs. an entry in fetchedSsccBreakdowns, even an empty array)
  // is what distinguishes "fetch failed" from "fetch succeeded with no
  // rows" — the two must render differently (Retry button vs. "No data
  // present").
  const [ssccErrors, setSsccErrors] = useState<Record<string, string>>({});
  const [internalPage, setInternalPage] = useState<number>(1);
  const [internalPageSize, setInternalPageSize] = useState<25 | 50 | 100>(25);

  // Collapse all expanded rows when the applied filters change
  const appliedFiltersKey = useMemo(
    () => JSON.stringify(appliedFilters ?? {}),
    [appliedFilters]
  );

  useEffect(() => {
    setExpandedRows({});
  }, [appliedFiltersKey]);

  // Export functionality
  const { isExporting, exportLoadExecutionData } = useLoadExecutionExport();
  const { showToast } = useToast();

  // Determine whether to use API data or mock data
  // Use API data if:
  // 1. onSortProp callback is provided (indicates parent wants to use API), OR
  // 2. Detail records are already loaded (API data exists)
  // This ensures sort events trigger API calls even before data arrives
  const useApiData = !!onSortProp || (!!detailRecords && detailRecords.length > 0);
  // API mode is driven by parent (pageNumber/pageSize props); mock mode by local state.
  const currentPage = useApiData ? pageNumber : internalPage;
  const currentPageSize = useApiData ? (pageSize || 25) : internalPageSize;

  // Convert API detail records to LoadExecutionRow format
  const apiDataAsRows = useMemo<LoadExecutionRow[]>(() => {
    if (!detailRecords || detailRecords.length === 0) return [];
    
    // Helper function to find location name from adminLocations if not provided by API
    const getLocationNameFromId = (locId: string): string | null => {
      if (!adminLocations || adminLocations.length === 0) return null;
      const foundLoc = adminLocations.find(
        (loc) => String(loc.locId || loc.locationId || "") === String(locId)
      );
      return foundLoc
        ? String(foundLoc.locName || foundLoc.locationName || foundLoc.name || "")
        : null;
    };
    
    return detailRecords.map((record) => {
      // ✅ FIX: Map location ID to location field, and locationName to locationName field
      // If locationName is missing from API, use location lookup as fallback
      const locationId = record.location || "";
      const locationNameFromApi = record.locationName || "";
      const locationNameFromLookup = getLocationNameFromId(locationId);
      const finalLocationName = locationNameFromApi || locationNameFromLookup || null;
      
      return {
        id: record.dispatchId,
        region: record.region || "",
        market: record.market || "",
        location: locationId, // ✅ FIX: Keep location as ID (from API)
        locationName: finalLocationName, // ✅ FIX: Map locationName from API, with lookup fallback
        locationType: "",
        driver: record.driver || "",
        status: record.status === "Complete" ? "Complete" : "Incomplete",
        onTime: record.onTime === "On Time" || record.onTime === "True",
        arrivedAsExpected: normalizeArrivedAsExpected(record.arrivedAsExpected),
        manifestHour: 0,
        deliveryDate: record.deliveryDate,
        localRouteId: record.localRouteId,
        sequence: record.sequence || null,
        nationalRouteId: record.nationalRouteId,
        manifestArrivalDateTime: record.driverManifestArrivalTime || "",
        actualArrivalDateTime: record.actualArrivalTime || "",
        actualCompletedDateTime: record.actualCompletedTime || "",
        highlightCompletionTime: record.highlightCompletionTime ?? false,
        completionTimeTooltip: record.completionTimeTooltip ?? null,
        completionTimeHighlightColor: record.completionTimeHighlightColor ?? null,
        palletCount: record.completedPallets ?? null,
        percentComplete: record.percentComplete,
        vehicle: "",
        orderCount: 0,
        ssccDetails: [],
      };
    });
  }, [detailRecords, adminLocations]);

  useEffect(() => {
    setExpandedRows({});
  }, [appliedFiltersKey]);

  // Use either API data or mock data
  const sourceRows = useApiData ? apiDataAsRows : rows;

  // Apply location-access gating to rows (mock mode: no filtering)
  const accessGatedRows = useMemo(
    () => gateLoadsByLocationAccess(sourceRows, accessibleLocationIds),
    [sourceRows, accessibleLocationIds]
  );
  
  const safeRows = useMemo<LoadExecutionRow[]>(() => {
    // Normalize and validate access-gated rows for safe usage
    return (Array.isArray(accessGatedRows) ? accessGatedRows : []).map((row, index) => ({
      id: String(row?.id ?? `LOAD-${index + 1}`),
      region: String(row?.region ?? ""),
      market: String(row?.market ?? ""),
      location: String(row?.location ?? ""),
      locationName: row?.locationName || null, // ✅ FIX: Preserve locationName through normalization
      locationType: String(row?.locationType ?? ""),
      driver: String(row?.driver ?? ""),
      status: row?.status === "Incomplete" ? "Incomplete" : "Complete",
      onTime: Boolean(row?.onTime),
      arrivedAsExpected:
        row?.arrivedAsExpected === "Y" ||
        row?.arrivedAsExpected === "N" ||
        row?.arrivedAsExpected === "Blank"
          ? row.arrivedAsExpected
          : "Blank",
      manifestHour: Number.isFinite(row?.manifestHour)
        ? Number(row.manifestHour)
        : 0,
      deliveryDate: String(row?.deliveryDate ?? ""),
      localRouteId: String(row?.localRouteId ?? ""),
      sequence: String(row?.sequence ?? "").trim() || null,
      nationalRouteId: String(row?.nationalRouteId ?? ""),
      manifestArrivalDateTime: String(row?.manifestArrivalDateTime ?? ""),
      actualArrivalDateTime: String(row?.actualArrivalDateTime ?? ""),
      actualCompletedDateTime: String(row?.actualCompletedDateTime ?? ""),
      highlightCompletionTime: Boolean(row?.highlightCompletionTime),
      completionTimeTooltip: row?.completionTimeTooltip ?? null,
      completionTimeHighlightColor: row?.completionTimeHighlightColor ?? null,
      palletCount: Number.isFinite(row?.palletCount)
        ? Number(row.palletCount)
        : null,
      percentComplete: Number.isFinite(row?.percentComplete)
        ? Number(row.percentComplete)
        : 0,
      vehicle: resolveVehicleId(row?.vehicle, String(row?.id ?? `LOAD-${index + 1}`)),
      orderCount: resolveOrderCount(row?.orderCount, String(row?.id ?? `LOAD-${index + 1}`)),
      // ⚠️ Mock/deterministic SSCC details are generated ONLY in local mock
      // mode (!useApiData — no backend wired up, e.g. a storybook/preview
      // usage of this component). In real API mode, ssccDetails must stay
      // empty here: the SSCC breakdown is fetched lazily, per dispatchId,
      // only when the user expands that row (see toggleExpand /
      // fetchSsccBreakdownForRow below). Generating fake data here for API
      // rows would silently mask real "successful response, no data" and
      // "request failed" states behind fabricated rows.
      ssccDetails: Array.isArray(row?.ssccDetails) && row.ssccDetails.length > 0
        ? row.ssccDetails.map((item, ssccIndex) => ({
            stop: Number.isFinite(item?.stop)
              ? Number(item.stop)
              : ssccIndex + 1,
            ssccId: String(item?.ssccId ?? ""),
            status: String(item?.status ?? "Not Loaded"),
            eta: String(item?.eta ?? ""),
          }))
        : !useApiData && row?.status === "Incomplete"
          ? generateDeterministicSsccDetails(String(row?.id ?? `LOAD-${index + 1}`), Number(row?.manifestHour) || 3)
          : [],
    }));
  }, [accessGatedRows, useApiData]);

  // The sort indicator (arrow) and the rows actually displayed must reflect
  // the sort that is *actually* applied. In API mode that's the
  // parent-owned apiSortBy/apiSortDirection (the local sortKey/sortDirection
  // state is only meaningful in mock/local mode); using a disconnected local
  // state here previously caused the header to show a column (Driver
  // Manifest Arrival Date/Time) as "actively sorted ascending" by default
  // even though no sort had been requested from the backend yet.
  const activeSortKey: SortKey | undefined = useApiData
    ? (apiSortBy as SortKey | undefined)
    : sortKey;
  const activeSortDirection: "asc" | "desc" | undefined = useApiData
    ? apiSortDirection
      ? (apiSortDirection.toLowerCase() as "asc" | "desc")
      : undefined
    : sortDirection;

  // Sort the rows for display.
  //
  // API mode: the backend paginates (25/50/100 rows per page out of 18,643+
  // total), so re-sorting a single page client-side can NEVER produce a
  // correct global sort — it can only reshuffle whatever 25 rows happen to
  // already be on this page. Doing so was actively masking a real backend
  // sort-mapping bug: the "location" column sorted by the raw location ID
  // field instead of locationName, and this client-side re-sort used the
  // exact same wrong field, so the visible order never reflected the
  // (fixed) backend order and made it impossible to tell whether the API
  // was sorting correctly. In API mode we now trust the server's row order
  // completely; the header arrow still reflects apiSortBy/apiSortDirection.
  //
  // Mock/local mode: all rows are available on the client, so sorting here
  // is both correct and necessary (there's no backend to do it).
  const sortedRows = useMemo(() => {
    if (useApiData) {
      return safeRows;
    }
    if (!activeSortKey) {
      return safeRows;
    }
    const dir = activeSortDirection === "desc" ? -1 : 1;
    const next = [...safeRows];
    next.sort((a, b) => {
      switch (activeSortKey) {
        case "id":
        case "deliveryDate":
        case "location":
        case "localRouteId":
        case "nationalRouteId":
        case "driver":
        case "manifestArrivalDateTime":
        case "actualArrivalDateTime":
        case "arrivedAsExpected":
        case "actualCompletedDateTime":
        case "status":
          return dir * String(a[activeSortKey]).localeCompare(String(b[activeSortKey]));
        case "onTime":
          return dir * (Number(a.onTime) - Number(b.onTime));
        case "palletCount":
          return dir * ((a.palletCount ?? -1) - (b.palletCount ?? -1));
        default:
          return dir * ((a[activeSortKey] as number) - (b[activeSortKey] as number));
      }
    });
    return next;
  }, [safeRows, activeSortKey, activeSortDirection, useApiData]);

  const totalPages = useApiData
    ? Math.max(1, Math.ceil((totalElements || 0) / currentPageSize))
    : Math.max(1, Math.ceil(sortedRows.length / currentPageSize));

  const paginatedRows = useMemo(() => {
    if (useApiData) {
      // For API data, just return the records as-is (pagination is handled by backend)
      return sortedRows;
    }
    // For mock data, apply pagination
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * currentPageSize;
    return sortedRows.slice(start, start + currentPageSize);
  }, [currentPage, currentPageSize, sortedRows, totalPages, useApiData]);

  const summaryCards = useMemo(() => {
    // Use API data if available, otherwise compute from rows
    let plannedLoads: number;
    let completeLoads: number;
    let incompleteLoads: number;
    let onTimeLoads: number;
    let lateLoads: number;
    let arrivedAsExpectedLoads: number;
    let arrivalOpportunities: number;
    let arrivalDenominator: number;

    if (scorecardData) {
      // Use API response data
      plannedLoads = scorecardData.plannedLoads ?? 0;
      completeLoads = scorecardData.completedLoads ?? 0;
      incompleteLoads = scorecardData.incompleteLoads ?? 0;
      onTimeLoads = scorecardData.onTimeLoads ?? 0;
      lateLoads = scorecardData.lateLoads ?? 0;
      arrivedAsExpectedLoads = scorecardData.driverArrivedAsExpectedCount ?? 0;
      arrivalOpportunities = scorecardData.driverArrivedAsExpectedDenominator ?? 0;
      arrivalDenominator = arrivalOpportunities;
    } else {
      // Fallback to computed values from rows
      plannedLoads = safeRows.length;
      completeLoads = safeRows.filter((row) => row.status === "Complete").length;
      incompleteLoads = safeRows.filter((row) => row.status === "Incomplete").length;
      onTimeLoads = safeRows.filter((row) => row.onTime).length;
      lateLoads = Math.max(plannedLoads - onTimeLoads, 0);
      arrivedAsExpectedLoads = safeRows.filter((row) => row.arrivedAsExpected === "Y").length;
      arrivalOpportunities = safeRows.filter((row) => row.arrivedAsExpected === "N").length;
      arrivalDenominator = arrivedAsExpectedLoads + arrivalOpportunities;
    }

    const pct = (numerator: number, denominator: number) =>
      denominator > 0 ? (numerator / denominator) * 100 : 0;
    const pctText = (value: number) => `${value.toFixed(2)}%`;
    const fmt = (value: number) => value.toLocaleString("en-US");

    return [
      {
        key: "complete",
        title: "Complete Loads",
        value: fmt(completeLoads),
        subText: `of ${fmt(plannedLoads)} planned loads · ${pctText(
          pct(completeLoads, plannedLoads)
        )}`,
        tooltip:
          "Actual completed trips against trips planned for the selected range. Target 99.50%",
        valueTone:
          pct(completeLoads, plannedLoads) >= 99.5 ? "positive" : "negative",
      },
      {
        key: "incomplete",
        title: "Incomplete Loads",
        value: fmt(incompleteLoads),
        subText: incompleteLoads === 0
          ? "No open loads"
          : "Loads with open SSCCs",
        tooltip:
          "Loads with open SSCCs.",
        valueTone: incompleteLoads > 0 ? "negative" : "positive",
      },
      {
        key: "onTime",
        title: "On-Time Loads",
        value: fmt(onTimeLoads),
        subText: `of ${fmt(plannedLoads)} planned loads · ${pctText(
          pct(onTimeLoads, plannedLoads)
        )}`,
        tooltip:
          "On time when the final Warehouse Load Time is before the actual Route Start Time. Target 98.50%.",
        valueTone:
          pct(onTimeLoads, plannedLoads) >= 98.5 ? "positive" : "negative",
      },
      {
        key: "late",
        title: "Late Loads",
        value: fmt(lateLoads),
        subText: "Included late-but-complete",
        tooltip:
          "Loads that did not leave by their planned dispatch time. A load can be late and complete.",
        valueTone: "negative",
      },
      {
        key: "dae",
        title: "Driver Arrived as Expected",
        value: fmt(arrivedAsExpectedLoads),
        subText: `of ${fmt(arrivalDenominator)} arrivals · ${pctText(
          pct(arrivedAsExpectedLoads, arrivalDenominator)
        )}`,
        tooltip:
          "Drivers arriving at or before their Manifest Arrival Time over drivers with a manifest time. Target 85.00%.",
        valueTone:
          pct(arrivedAsExpectedLoads, arrivalDenominator) >= 85
            ? "positive"
            : "negative",
      },
      {
        key: "opportunities",
        title: "Driver Arrival Opportunities",
        value: fmt(arrivalOpportunities),
        subText: "Arrivals after manifest time",
        tooltip:
          "Drivers arriving after their manifest arrival time. Blank manifest times are excluded from both measures.",
        valueTone: "negative",
      },
    ];
  }, [safeRows, scorecardData]);

  const onSort = (key: SortKey) => {
    if (useApiData) {
      // Drive the 3-click cycle off the real applied sort (apiSortBy/
      // apiSortDirection), not a local state copy that can drift out of sync.
      if (apiSortBy !== key) {
        onSortProp?.(key, "ASC");
      } else if (apiSortDirection === "ASC") {
        onSortProp?.(key, "DESC");
      } else if (apiSortDirection === "DESC") {
        onSortProp?.(null as any, null as any);
      } else {
        onSortProp?.(key, "ASC");
      }
      return;
    }

    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection("asc");
      setInternalPage(1);
      return;
    }

    if (sortDirection === "asc") {
      setSortDirection("desc");
      setInternalPage(1);
      return;
    }

    // Third click resets to mockup default sort state.
    setSortKey("manifestArrivalDateTime");
    setSortDirection("asc");
    setInternalPage(1);
  };

  const renderSortHeader = (label: string, key: SortKey) => {
    const isActiveSort = activeSortKey === key;
    return (
      <View style={styles.sortHeaderContent}>
        <Text
          style={[
            styles.headerCellText,
            isDark && { color: '#9A9A9A' },
            isActiveSort && styles.headerCellTextActive,
            isActiveSort && isDark && { color: '#3D8BFF' },
          ]}
        >
          {label}
        </Text>
        <MaterialIcons
          name={
            isActiveSort
              ? activeSortDirection === "asc"
                ? "keyboard-arrow-up"
                : "keyboard-arrow-down"
              : "unfold-more"
          }
          size={14}
          color={isActiveSort ? (isDark ? '#3D8BFF' : '#0B5FC6') : (isDark ? '#6B7280' : '#98A2B3')}
          style={styles.sortIcon}
        />
      </View>
    );
  };

  // Fetches (or re-fetches, via the Retry button) the SSCC breakdown for a
  // single dispatchId. This is the ONLY place that calls
  // fetchLoadExecutionSsccBreakdown — it is never called on page load,
  // only from toggleExpand (first expand) or a Retry button press.
  const fetchSsccBreakdownForRow = useCallback((rowId: string) => {
    // Clear any previous error so the panel shows the loading state again
    // while this (re)attempt is in flight.
    setSsccErrors((prev) => {
      if (!(rowId in prev)) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setFetchingSsccMap((f) => ({ ...f, [rowId]: true }));
    fetchLoadExecutionSsccBreakdown(rowId)
      .then((items) => {
        const breakdownItems: LoadExecutionSsccDetail[] = items.map((item, idx) => ({
          stop: item.stop ?? idx + 1,
          ssccId: item.ssccId || "",
          eta: item.eta ?? "",
          status: item.status ?? "",
        }));
        // Store the result even when it's an empty array — an empty array
        // (vs. `undefined`) is what marks this dispatchId as "successfully
        // fetched", which is what lets the panel show "No data present"
        // instead of a perpetual loading spinner.
        setFetchedSsccBreakdowns((b) => ({ ...b, [rowId]: breakdownItems }));
      })
      .catch((err) => {
        console.error(
          `[LoadExecution] Failed to load SSCC breakdown for ${rowId}:`,
          err
        );
        // Do NOT fall back to mock/deterministic data on failure — the
        // panel renders an error message + Retry button instead (see
        // renderSsccPanelContent).
        setSsccErrors((e) => ({
          ...e,
          [rowId]: "Unable to load SSCC details.",
        }));
      })
      .finally(() => {
        setFetchingSsccMap((f) => ({ ...f, [rowId]: false }));
      });
  }, []);

  const toggleExpand = useCallback(
    (rowKey: string, rowId: string, isExpandable: boolean) => {
      // Both Complete and Incomplete loads can expand to show their SSCC
      // breakdown (fetched from the same API, keyed only by dispatchId).
      // This guard just protects against unexpected non-expandable rows.
      if (!isExpandable) return;
      setExpandedRows((prev) => {
        const willExpand = !prev[rowKey];
        // Fetch exactly once per dispatchId on first expand:
        // - fetchedSsccBreakdowns[rowId] === undefined → never fetched yet
        // - ssccErrors[rowId] === undefined → not sitting in an error state
        //   (re-fetching an errored row only happens via the explicit
        //   Retry button, never automatically by collapsing/re-expanding)
        // - !fetchingSsccMap[rowId] → not already in flight
        if (
          willExpand &&
          fetchedSsccBreakdowns[rowId] === undefined &&
          ssccErrors[rowId] === undefined &&
          !fetchingSsccMap[rowId]
        ) {
          fetchSsccBreakdownForRow(rowId);
        }
        return { ...prev, [rowKey]: willExpand };
      });
    },
    [fetchedSsccBreakdowns, fetchingSsccMap, ssccErrors, fetchSsccBreakdownForRow]
  );

  const getSsccStatusTagStyle = (status: string, isDarkTheme: boolean) => {
    const norm = (status || "").trim().toLowerCase();
    if (norm === "not loaded") {
      return {
        tagStyle: isDarkTheme ? { backgroundColor: "#3B1818" } : styles.tagLateCustom,
        tagTextStyle: isDarkTheme ? { color: "#FCA5A5" } : styles.tagLateCustomText,
      };
    }
    if (norm === "picking" || norm === "in progress" || norm === "loading") {
      return {
        tagStyle: isDarkTheme ? { backgroundColor: "#172E47" } : { backgroundColor: "#E0F2FE" },
        tagTextStyle: isDarkTheme ? { color: "#93C5FD" } : { color: "#0369A1" },
      };
    }
    if (norm === "loaded" || norm === "complete" || norm === "completed") {
      return {
        tagStyle: isDarkTheme ? { backgroundColor: "#143322" } : styles.tagOnTimeCustom,
        tagTextStyle: isDarkTheme ? { color: "#86EFAC" } : styles.tagOnTimeCustomText,
      };
    }
    return {
      tagStyle: isDarkTheme ? { backgroundColor: "#3B1818" } : styles.tagLateCustom,
      tagTextStyle: isDarkTheme ? { color: "#FCA5A5" } : styles.tagLateCustomText,
    };
  };

  // Mock export for local data (fallback when API not available)
  const handleMockExport = () => {
    const header = [
      "DISPATCH ID",
      "DELIVERY DATE",
      "LOCATION",
      "LOCAL ROUTE ID + SEQUENCE",
      "NATIONAL ROUTE ID",
      "DRIVER",
      "DRIVER MANIFEST ARRIVAL DATE/TIME",
      "ACTUAL ARRIVAL DATE/TIME",
      "ARRIVED AS EXPECTED",
      "ACTUAL COMPLETED DATE/TIME",
      "% COMPLETE",
      "ON-TIME",
      "STATUS",
      "STOP #",
      "SSCC",
      "EST. COMPLETION",
      "SSCC STATUS",
    ];

    const lines = sortedRows.flatMap((row) => {
      const baseLine = [
        row.id,
        row.deliveryDate,
        row.locationName || row.location,
        formatLocalRouteWithSequence(row.localRouteId, row.sequence),
        row.nationalRouteId,
        row.driver,
        row.manifestArrivalDateTime,
        row.actualArrivalDateTime,
        row.arrivedAsExpected,
        row.actualCompletedDateTime,
        `${row.percentComplete.toFixed(2)}%`,
        row.onTime ? "On Time" : "Late",
        row.status,
      ];

      if (row.status !== "Incomplete" || row.ssccDetails.length === 0) {
        return [[...baseLine, "", "", "", ""].map(csvEscape).join(",")];
      }

      return row.ssccDetails.map((sscc) =>
        [
          ...baseLine,
          sscc.stop,
          sscc.ssccId,
          sscc.eta,
          sscc.status,
        ]
          .map(csvEscape)
          .join(",")
      );
    });

    const csv = [header.join(","), ...lines].join("\n");

    if (Platform.OS === "web" && typeof document !== "undefined") {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      const exportFileName = `OnTimeDispatch_Loads_${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.download = exportFileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      showToast({
        header: 'Export started',
        subtext: exportFileName,
        variant: 'success',
        duration: 3000,
      });
    }
  };

  // Export handler that uses API if filters provided, otherwise uses mock export
  const handleExportClick = useCallback(async () => {
    // Use API export if filters and locations provided; otherwise use mock export
    if (appliedFilters && adminLocations) {
      console.log('[Load Execution] Triggering API export');
      await exportLoadExecutionData(appliedFilters, adminLocations, apiSortBy, apiSortDirection);
    } else {
      console.log('[Load Execution] Triggering mock CSV export (no API data)');
      handleMockExport();
    }
  }, [appliedFilters, adminLocations, apiSortBy, apiSortDirection, exportLoadExecutionData]);

  const totalCount = useApiData ? totalElements : sortedRows.length;

  const handlePrevPage = useCallback(() => {
    if (useApiData) {
      onPageChange?.(Math.max(1, currentPage - 1));
    } else {
      setInternalPage((p) => Math.max(1, p - 1));
    }
  }, [useApiData, currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (useApiData) {
      onPageChange?.(Math.min(totalPages, currentPage + 1));
    } else {
      setInternalPage((p) => Math.min(totalPages, p + 1));
    }
  }, [useApiData, currentPage, totalPages, onPageChange]);

  const handlePageSizeSelect = useCallback(
    (size: 25 | 50 | 100) => {
      if (useApiData) {
        onPageSizeChange?.(size);
      } else {
        setInternalPageSize(size);
        setInternalPage(1);
      }
    },
    [useApiData, onPageSizeChange]
  );

  const currentStart =
    totalCount === 0 ? 0 : Math.min((currentPage - 1) * currentPageSize + 1, totalCount);
  const currentEnd = Math.min(currentPage * currentPageSize, totalCount);

  // ──────────────────────────────────────────────────────────────────────
  // Shared render helpers for the sticky-left-column (iPad/mobile) layout.
  // These render exactly the same "remaining" (non-sticky) columns and
  // SSCC panel markup as the desktop table below, just factored out so the
  // split layout can reuse them without duplicating ~150 lines of JSX.
  // ──────────────────────────────────────────────────────────────────────
  const renderRemainingHeaderCells = () => (
    <>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.deliveryDate }]}
        onPress={() => onSort("deliveryDate")}
      >
        {renderSortHeader("DELIVERY\nDATE", "deliveryDate")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.location }]}
        onPress={() => onSort("location")}
      >
        {renderSortHeader("LOCATION", "location")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.localRouteId }]}
        onPress={() => onSort("localRouteId")}
      >
        {renderSortHeader("LOCAL ROUTE ID\n+ SEQUENCE", "localRouteId")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.nationalRouteId }]}
        onPress={() => onSort("nationalRouteId")}
      >
        {renderSortHeader("NATIONAL\nROUTE ID", "nationalRouteId")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.driver }]}
        onPress={() => onSort("driver")}
      >
        {renderSortHeader("DRIVER", "driver")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.manifestArrivalDateTime }]}
        onPress={() => onSort("manifestArrivalDateTime")}
      >
        {renderSortHeader(
          "DRIVER MANIFEST\nARRIVAL DATE/TIME",
          "manifestArrivalDateTime"
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualArrivalDateTime }]}
        onPress={() => onSort("actualArrivalDateTime")}
      >
        {renderSortHeader(
          "ACTUAL ARRIVAL\nDATE/TIME",
          "actualArrivalDateTime"
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.arrivedAsExpected }]}
        onPress={() => onSort("arrivedAsExpected")}
      >
        {renderSortHeader("ARRIVED AS\nEXPECTED", "arrivedAsExpected")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualCompletedDateTime }]}
        onPress={() => onSort("actualCompletedDateTime")}
      >
        {renderSortHeader(
          "ACTUAL COMPLETED\nDATE/TIME",
          "actualCompletedDateTime"
        )}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.palletCount }]}
        onPress={() => onSort("palletCount")}
      >
        {renderSortHeader("PALLET COUNT", "palletCount")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.percentComplete }]}
        onPress={() => onSort("percentComplete")}
      >
        {renderSortHeader("% COMPLETE", "percentComplete")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.onTime }]}
        onPress={() => onSort("onTime")}
      >
        {renderSortHeader("ON-TIME", "onTime")}
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.status, paddingRight: 2 }]}
        onPress={() => onSort("status")}
      >
        {renderSortHeader("STATUS", "status")}
      </TouchableOpacity>
    </>
  );

  const renderRemainingBodyCells = (row: LoadExecutionRow) => {
    const tagStyle =
      row.status === "Complete" ? styles.tagCompleteCustom : styles.tagRed;
    const tagTextStyle =
      row.status === "Complete" ? styles.tagCompleteCustomText : styles.tagText;
    const isBlankDriver = !row.driver;
    const isBlankManifestArrivalDateTime = !row.manifestArrivalDateTime;
    const isBlankActualArrivalDateTime = !row.actualArrivalDateTime;
    const isBlankOnTime =
      isBlankManifestArrivalDateTime || isBlankActualArrivalDateTime;
    const onTimeTagStyle = isBlankOnTime
      ? styles.tagNeutral
      : row.onTime
      ? styles.tagOnTimeCustom
      : styles.tagLateCustom;
    const onTimeTagTextStyle = isBlankOnTime
      ? styles.tagText
      : row.onTime
      ? styles.tagOnTimeCustomText
      : styles.tagLateCustomText;
    return (
      <>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.deliveryDate }]}>
          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>{row.deliveryDate}</Text>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.location }]}>
          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>{row.locationName || row.location}</Text>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.localRouteId }]}>
          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
            {formatLocalRouteWithSequence(row.localRouteId, row.sequence)}
          </Text>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.nationalRouteId }]}>
          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
            {row.nationalRouteId}
          </Text>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.driver }]}>
          {isBlankDriver ? (
            <View style={[styles.tag, styles.tagNeutral]}>
              <Text style={styles.tagText}>-</Text>
            </View>
          ) : (
            <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>{row.driver}</Text>
          )}
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.manifestArrivalDateTime }]}>
          {isBlankManifestArrivalDateTime ? (
            <View style={[styles.tag, styles.tagNeutral]}>
              <Text style={styles.tagText}>-</Text>
            </View>
          ) : (
            <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
              {row.manifestArrivalDateTime}
            </Text>
          )}
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualArrivalDateTime }]}>
          {isBlankActualArrivalDateTime ? (
            <View style={[styles.tag, styles.tagNeutral]}>
              <Text style={styles.tagText}>-</Text>
            </View>
          ) : (
            <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
              {row.actualArrivalDateTime}
            </Text>
          )}
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.arrivedAsExpected }]}>
          {row.arrivedAsExpected === "Blank" ? (
            <View style={[styles.tag, styles.tagNeutral]}>
              <Text style={styles.tagText}>-</Text>
            </View>
          ) : row.arrivedAsExpected === "Y" ? (
            <View style={[styles.tag, styles.tagArrivedYes]}>
              <Text style={styles.tagArrivedYesText}>
                {row.arrivedAsExpected}
              </Text>
            </View>
          ) : (
            <View style={[styles.tag, styles.tagRed]}>
              <Text style={styles.tagText}>
                {row.arrivedAsExpected}
              </Text>
            </View>
          )}
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualCompletedDateTime }]}>
          <View style={styles.completionTimeWrap}>
            <View
              testID={row.highlightCompletionTime ? `load-execution-completion-time-${row.id}` : undefined}
              style={[
                row.highlightCompletionTime && styles.completionTimeHighlight,
                row.highlightCompletionTime && row.completionTimeHighlightColor
                  ? { backgroundColor: row.completionTimeHighlightColor }
                  : null,
                row.highlightCompletionTime && isDark && styles.completionTimeHighlightDark,
              ]}
            >
              <Text
                style={[
                  styles.bodyText,
                  isDark && { color: '#D0D0D0' },
                  row.highlightCompletionTime && isDark && styles.completionTimeHighlightDarkText,
                ]}
              >
                {row.actualCompletedDateTime || "-"}
              </Text>
            </View>
            {row.highlightCompletionTime && row.completionTimeTooltip ? (
              <DLOTooltip
                content={row.completionTimeTooltip}
                position="top"
                trigger={summaryTooltipTrigger}
                maxWidth={280}
              >
                <View accessibilityLabel="Completion time information">
                  <MaterialIcons
                    name="info-outline"
                    size={15}
                    color={isDark ? '#9A9A9A' : '#667085'}
                  />
                </View>
              </DLOTooltip>
            ) : null}
          </View>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.palletCount }]}>
          <Text
            testID={`load-execution-pallet-count-${row.id}`}
            style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}
          >
            {row.palletCount == null ? "-" : row.palletCount}
          </Text>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.percentComplete }]}>
          <View style={styles.barWrap}>
            <View style={[styles.barTrack, isDark && { backgroundColor: '#3A3A3A' }]}>
              <View
                style={[
                  styles.barFill,
                  row.percentComplete < 100 && styles.barFillNegative,
                  {
                    width: `${Math.max(0, Math.min(100, row.percentComplete))}%`,
                  },
                ]}
              />
            </View>
            <Text
              style={[
                styles.bodyText,
                row.percentComplete >= 100 ? styles.onTimePositive : styles.onTimeNegative,
              ]}
            >
              {row.percentComplete.toFixed(0)}%
            </Text>
          </View>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.onTime }]}>
          <View style={[styles.tag, onTimeTagStyle]}>
            <Text style={onTimeTagTextStyle}>
              {isBlankOnTime ? "-" : row.onTime ? "On Time" : "Late"}
            </Text>
          </View>
        </View>
        <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.status, paddingRight: 2 }]}>
          <View style={[styles.tag, tagStyle]}>
            <Text style={tagTextStyle}>{row.status}</Text>
          </View>
        </View>
      </>
    );
  };

  const renderSsccPanelContent = (
    row: LoadExecutionRow,
    rowKey: string,
    onPanelLayout?: (height: number) => void
  ) => {
    const isLoading = !!fetchingSsccMap[row.id];
    const errorMessage = ssccErrors[row.id];
    const fetchedDetails = fetchedSsccBreakdowns[row.id];
    // An entry in fetchedSsccBreakdowns (even `[]`) means the request
    // already succeeded; `undefined` means it hasn't resolved yet (still
    // loading, or hasn't been kicked off for some reason).
    const hasFetchedData = Array.isArray(fetchedDetails);
    const ssccDetails = hasFetchedData ? fetchedDetails! : [];

    return (
      <View
        style={[styles.ssccPanel, isDark && { backgroundColor: '#252525', borderColor: '#404040' }]}
        onLayout={
          onPanelLayout
            ? (e) => onPanelLayout(e.nativeEvent.layout.height)
            : undefined
        }
      >
        {isLoading ? (
          <View style={styles.ssccStateWrap}>
            <ActivityIndicator size="small" color={isDark ? '#3D8BFF' : '#0B5FC6'} />
            <Text style={[styles.ssccStateText, isDark && { color: '#9A9A9A' }]}>
              Loading SSCC details…
            </Text>
          </View>
        ) : errorMessage ? (
          <View style={styles.ssccStateWrap}>
            <Text style={[styles.ssccErrorText, isDark && { color: '#FCA5A5' }]}>
              {errorMessage}
            </Text>
            <TouchableOpacity
              style={[styles.ssccRetryBtn, isDark && { borderColor: '#3D8BFF' }]}
              onPress={() => fetchSsccBreakdownForRow(row.id)}
              activeOpacity={0.7}
            >
              <MaterialIcons name="refresh" size={14} color={isDark ? '#3D8BFF' : '#0B5FC6'} />
              <Text style={[styles.ssccRetryBtnText, isDark && { color: '#3D8BFF' }]}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : !hasFetchedData ? (
          // Covers the brief instant between a row becoming expanded and the
          // fetch's `fetchingSsccMap` flag actually flipping to true.
          <View style={styles.ssccStateWrap}>
            <ActivityIndicator size="small" color={isDark ? '#3D8BFF' : '#0B5FC6'} />
          </View>
        ) : ssccDetails.length === 0 ? (
          // Successful response, zero rows — show this, never mock data.
          <View style={styles.ssccStateWrap}>
            <Text style={[styles.ssccEmptyText, isDark && { color: '#9A9A9A' }]}>
              No data present
            </Text>
          </View>
        ) : (
          <>
            <View style={[styles.ssccHeaderRow, isDark && { borderBottomColor: '#404040' }]}>
              <Text style={[styles.ssccHeaderCell, isDark && { color: '#9A9A9A' }, { width: 70 }]}>STOP #</Text>
              <Text style={[styles.ssccHeaderCell, isDark && { color: '#9A9A9A' }, { width: 320 }]}>SSCC</Text>
              <Text style={[styles.ssccHeaderCell, isDark && { color: '#9A9A9A' }, { width: 160 }]}>EST. COMPLETION</Text>
              <Text style={[styles.ssccHeaderCell, isDark && { color: '#9A9A9A' }, { width: 120 }]}>STATUS</Text>
            </View>
            {ssccDetails.map((sscc, idx) => (
              <View key={`${rowKey}-${sscc.ssccId || idx}`} style={[styles.ssccBodyRow, isDark && { borderBottomColor: '#363636' }]}>
                <Text style={[styles.ssccBodyCell, isDark && { color: '#D0D0D0' }, { width: 70 }]}>{sscc.stop}</Text>
                <Text style={[styles.ssccBodyCell, isDark && { color: '#D0D0D0' }, { width: 320 }]}>{sscc.ssccId || '-'}</Text>
                <Text style={[styles.ssccBodyCell, isDark && { color: '#D0D0D0' }, { width: 160 }]}>{sscc.eta || '-'}</Text>
                <View style={{ width: 120, justifyContent: "center" }}>
                  <View style={[styles.tag, sscc.status === "Completed" ? styles.tagArrivedYes : sscc.status === "Picking" ? styles.tagNeutral : styles.tagRed]}>
                    <Text style={styles.tagText}>{sscc.status || '-'}</Text>
                  </View>
                </View>
              </View>
            ))}
          </>
        )}
      </View>
    );
  };

  return (
    <View>
      {/* ✅ Mobile view for phones (iOS with width < 768) */}
      {isMobilePhone ? (
        <LoadExecutionMobileCards
          rows={safeRows as any}
          loading={detailLoading}
          error={detailError}
          onExpandCard={undefined}
          isDark={isDarkPhone}
        />
      ) : (
        /* Desktop/Tablet view */
        <View>
          <View style={styles.summaryGrid}>
            {summaryCards.map((card) => (
              <View
                key={card.key}
                style={[
                  styles.summaryCard,
                  isDark && { backgroundColor: '#202020', borderColor: '#404040' },
                ]}
              >
                <View style={styles.summaryHeaderRow}>
                  <Text style={[styles.summaryLabel, isDark && { color: '#9A9A9A' }]}>{card.title}</Text>
                  <DLOTooltip
                    title={undefined}
                    content={card.tooltip}
                    position="top"
                    trigger={summaryTooltipTrigger}
                    maxWidth={260}
                    padding={10}
                    fontSize={11}
                    verticalOffset={8}
                  >
                    <View style={styles.summaryInfoDot}>
                      <Text style={styles.summaryInfoDotText}>i</Text>
                    </View>
                  </DLOTooltip>
                </View>
                <Text
                  style={[
                    styles.summaryValue,
                    card.valueTone === "positive" && styles.summaryValuePositive,
                    card.valueTone === "negative" && styles.summaryValueNegative,
                    isDark && card.valueTone !== "positive" && card.valueTone !== "negative" && { color: '#F2F2F2' },
                  ]}
                >
                  {card.value}
                </Text>
                <Text style={[styles.summarySubText, isDark && { color: '#9A9A9A' }]}>{card.subText}</Text>
              </View>
            ))}
        </View>

      <View style={[styles.card, isDark && { backgroundColor: '#1E1E1E', borderColor: '#363636' }]}>
        <View style={styles.headerRow}>
          <View>
            <Text style={[styles.title, isDark && { color: '#F2F2F2' }]}>Load Execution Detail</Text>
            <Text style={[styles.subtitle, isDark && { color: '#9A9A9A' }]}>
              Arrived as Expected is Y when the driver arrived no earlier than
              30 minutes before the manifest time. Expand an incomplete load for
              its open SSCCs.
            </Text>
          </View>
          {Platform.OS === 'web' && (
            <TouchableOpacity
              style={[styles.exportBtn, isDark && { backgroundColor: '#252525', borderColor: '#3D8BFF' }]}
              onPress={handleExportClick}
              disabled={isExporting}
              activeOpacity={isExporting ? 1 : 0.7}
            >
              <MaterialIcons
                name="download"
                size={16}
                color={isExporting ? '#6B7280' : (isDark ? '#3D8BFF' : '#0B5FC6')}
              />
              <Text style={[
                styles.exportBtnText,
                isDark && { color: '#3D8BFF' },
                isExporting && { color: '#6B7280' }
              ]}>
                {isExporting ? 'Exporting...' : 'Export Data'}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.outerContainer}>
          {/* Always replace table rows with skeleton while detail API fetch is in flight.
              This covers initial load and refresh actions (sort/page/filter/search). */}
          {detailLoading ? (
            <LoadExecutionTableSkeleton rowCount={8} isDark={isDark} />
          ) : useStickyIdColumn ? (
            /* ✅ Sticky Dispatch ID column layout (iPad & mobile breakpoints,
               width < 1024). The Expand + Dispatch ID columns are rendered
               in a fixed left column OUTSIDE the horizontal ScrollView, so
               they stay visible while the rest of the columns scroll behind
               them — this works natively on iOS/Android (not just web)
               since it doesn't rely on a CSS `position: sticky` style. */
            <View style={styles.stickyTableRow}>
              <View
                style={[
                  styles.stickyLeftCol,
                  { width: LOAD_EXECUTION_STICKY_LEFT_WIDTH },
                  isDark && styles.stickyLeftColDark,
                ]}
              >
                <View
                  style={[
                    styles.headerCellsRow,
                    isDark && { backgroundColor: '#252525', borderColor: '#363636' },
                  ]}
                >
                  <View style={[styles.cell, styles.leftCell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.expand }]}>
                    <Text style={styles.headerCellText} />
                  </View>
                  <TouchableOpacity
                    style={[styles.cell, styles.leftCell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.id }]}
                    onPress={() => onSort("id")}
                  >
                    {renderSortHeader("DISPATCH ID", "id")}
                  </TouchableOpacity>
                </View>

                {sortedRows.length === 0 ? (
                  <View style={styles.emptyTableState} />
                ) : (
                  paginatedRows.map((row, rowIndex) => {
                    const rowKey = `${row.id}-${rowIndex}`;
                    const isExpanded = !!expandedRows[rowKey];
                    const panelHeight = expandedRowHeights[rowKey];
                    // SSCC breakdown expansion is available for every load
                    // (Complete or Incomplete) — both fetch from the same
                    // sscc-breakdown API keyed only by dispatchId.
                    const isExpandable = true;
                    return (
                      <View key={rowKey}>
                        <TouchableOpacity
                          style={[
                            styles.bodyRow,
                            isDark && { backgroundColor: '#1E1E1E', borderBottomColor: '#303030' },
                            rowIndex % 2 === 1 && styles.bodyRowAlt,
                            rowIndex % 2 === 1 && isDark && { backgroundColor: '#242424' },
                          ]}
                          onPress={() => toggleExpand(rowKey, row.id, isExpandable)}
                          activeOpacity={isExpandable ? 0.7 : 1}
                        >
                          <View style={[styles.cell, styles.leftCell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.expand }]}>
                            <View style={styles.expandLabelWrap}>
                              {isExpandable && (
                                <MaterialIcons
                                  name={isExpanded ? "keyboard-arrow-down" : "chevron-right"}
                                  size={16}
                                  color={isDark ? "#9A9A9A" : "#6B7280"}
                                />
                              )}
                            </View>
                          </View>
                          <View style={[styles.cell, styles.leftCell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.id }]}>
                            <Text style={[styles.linkText, isDark && { color: '#3D8BFF' }]}>{row.id}</Text>
                          </View>
                        </TouchableOpacity>
                        {/* Blank spacer matching the measured height of the
                            SSCC panel rendered on the scrollable side, so
                            rows stay aligned between the two halves. */}
                        {isExpanded && isExpandable && (
                          <View
                            style={[
                              styles.stickyPanelSpacer,
                              { height: panelHeight ?? 0 },
                              isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                            ]}
                          />
                        )}
                      </View>
                    );
                  })
                )}
              </View>

              <ScrollView horizontal showsHorizontalScrollIndicator style={styles.stickyScrollArea}>
                <View style={[styles.stickyScrollContent, { minWidth: LOAD_EXECUTION_SCROLLABLE_MIN_WIDTH }]}>
                  <View
                    style={[
                      styles.headerCellsRow,
                      isDark && { backgroundColor: '#252525', borderColor: '#363636' },
                    ]}
                  >
                    {renderRemainingHeaderCells()}
                  </View>

                  {sortedRows.length === 0 ? (
                    <View style={styles.emptyTableState}>
                      <View style={styles.emptyWrap}>
                        <Text style={[styles.emptyTitle, isDark && { color: '#F2F2F2' }]}>No loads match the current filters.</Text>
                        <Text style={[styles.emptyText, isDark && { color: '#9A9A9A' }]}>Adjust the filter bar or select Reset.</Text>
                      </View>
                    </View>
                  ) : (
                    paginatedRows.map((row, rowIndex) => {
                      const rowKey = `${row.id}-${rowIndex}`;
                      const isExpanded = !!expandedRows[rowKey];
                      return (
                        <View key={rowKey}>
                          <View
                            style={[
                              styles.bodyRow,
                              isDark && { backgroundColor: '#1E1E1E', borderBottomColor: '#303030' },
                              rowIndex % 2 === 1 && styles.bodyRowAlt,
                              rowIndex % 2 === 1 && isDark && { backgroundColor: '#242424' },
                            ]}
                          >
                            {renderRemainingBodyCells(row)}
                          </View>
                          {isExpanded &&
                            renderSsccPanelContent(row, rowKey, (height) => {
                              setExpandedRowHeights((prev) =>
                                prev[rowKey] === height ? prev : { ...prev, [rowKey]: height }
                              );
                            })}
                        </View>
                      );
                    })
                  )}
                </View>
              </ScrollView>
            </View>
          ) : (
            <View style={styles.tableContainer}>
              <ScrollView horizontal showsHorizontalScrollIndicator>
              <View style={[styles.tableWrap, { minWidth: LOAD_EXECUTION_TABLE_MIN_WIDTH }]}>
              <View style={[styles.headerCellsRow, isDark && { backgroundColor: '#252525', borderColor: '#363636' }]}>
                <View
                  style={[
                    styles.cell,
                    styles.leftCell,
                    { width: LOAD_EXECUTION_COLUMN_WIDTHS.expand },
                  ]}
                >
                  <Text style={styles.headerCellText} />
                </View>
                <TouchableOpacity
                  style={[
                    styles.cell,
                    styles.leftCell,
                    { width: LOAD_EXECUTION_COLUMN_WIDTHS.id },
                  ]}
                  onPress={() => onSort("id")}
                >
                  {renderSortHeader("DISPATCH ID", "id")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.deliveryDate }]}
                  onPress={() => onSort("deliveryDate")}
                >
                  {renderSortHeader("DELIVERY\nDATE", "deliveryDate")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.location }]}
                  onPress={() => onSort("location")}
                >
                  {renderSortHeader("LOCATION", "location")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.localRouteId }]}
                  onPress={() => onSort("localRouteId")}
                >
                  {renderSortHeader("LOCAL ROUTE ID\n+ SEQUENCE", "localRouteId")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.nationalRouteId }]}
                  onPress={() => onSort("nationalRouteId")}
                >
                  {renderSortHeader("NATIONAL\nROUTE ID", "nationalRouteId")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.driver }]}
                  onPress={() => onSort("driver")}
                >
                  {renderSortHeader("DRIVER", "driver")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.manifestArrivalDateTime }]}
                  onPress={() => onSort("manifestArrivalDateTime")}
                >
                  {renderSortHeader(
                    "DRIVER MANIFEST\nARRIVAL DATE/TIME",
                    "manifestArrivalDateTime"
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualArrivalDateTime }]}
                  onPress={() => onSort("actualArrivalDateTime")}
                >
                  {renderSortHeader(
                    "ACTUAL ARRIVAL\nDATE/TIME",
                    "actualArrivalDateTime"
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.arrivedAsExpected }]}
                  onPress={() => onSort("arrivedAsExpected")}
                >
                  {renderSortHeader("ARRIVED AS\nEXPECTED", "arrivedAsExpected")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualCompletedDateTime }]}
                  onPress={() => onSort("actualCompletedDateTime")}
                >
                  {renderSortHeader(
                    "ACTUAL COMPLETED\nDATE/TIME",
                    "actualCompletedDateTime"
                  )}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.palletCount }]}
                  onPress={() => onSort("palletCount")}
                >
                  {renderSortHeader("PALLET COUNT", "palletCount")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.percentComplete }]}
                  onPress={() => onSort("percentComplete")}
                >
                  {renderSortHeader("% COMPLETE", "percentComplete")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.onTime }]}
                  onPress={() => onSort("onTime")}
                >
                  {renderSortHeader("ON-TIME", "onTime")}
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.status, paddingRight: 2 }]}
                  onPress={() => onSort("status")}
                >
                  {renderSortHeader("STATUS", "status")}
                </TouchableOpacity>
              </View>

              {sortedRows.length === 0 ? (
                <View style={styles.emptyTableState}>
                  <View style={styles.emptyWrap}>
                    <Text style={[styles.emptyTitle, isDark && { color: '#F2F2F2' }]}>No loads match the current filters.</Text>
                    <Text style={[styles.emptyText, isDark && { color: '#9A9A9A' }]}>Adjust the filter bar or select Reset.</Text>
                  </View>
                </View>
              ) : paginatedRows.map((row, rowIndex) => {
                // ⚠️ dispatchId (row.id) is not guaranteed unique — the backend
                // has confirmed responses can contain repeated dispatch IDs.
                // Using row.id alone as the React key causes duplicate keys
                // within the same page, which makes React's reconciler match
                // fibers ambiguously (both within this render and against the
                // previous render's rows), and can leave stale row content on
                // screen after a new sorted/paginated response arrives even
                // though the underlying data was replaced correctly. Scoping
                // the key to this row's position in the current page
                // (rowIndex) guarantees uniqueness per render and forces
                // every row to always reflect the row at that position in
                // the latest response — no records are hidden or merged.
                const rowKey = `${row.id}-${rowIndex}`;
                const isExpanded = !!expandedRows[rowKey];
                // SSCC breakdown expansion is available for every load
                // (Complete or Incomplete) — both fetch from the same
                // sscc-breakdown API keyed only by dispatchId.
                const isExpandable = true;
                // ✅ UPDATED: Custom status tag style for Complete
                const tagStyle =
                  row.status === "Complete" ? styles.tagCompleteCustom : styles.tagRed;
                const tagTextStyle =
                  row.status === "Complete" ? styles.tagCompleteCustomText : styles.tagText;
                const isBlankDriver = !row.driver;
                const isBlankManifestArrivalDateTime =
                  !row.manifestArrivalDateTime;
                const isBlankActualArrivalDateTime =
                  !row.actualArrivalDateTime;
                const isBlankOnTime =
                  isBlankManifestArrivalDateTime || isBlankActualArrivalDateTime;
                // ✅ UPDATED: Custom on-time tag styles
                const onTimeTagStyle = isBlankOnTime
                  ? styles.tagNeutral
                  : row.onTime
                  ? styles.tagOnTimeCustom
                  : styles.tagLateCustom;
                const onTimeTagTextStyle = isBlankOnTime
                  ? styles.tagText
                  : row.onTime
                  ? styles.tagOnTimeCustomText
                  : styles.tagLateCustomText;
                return (
                  <View key={rowKey}>
                    <View
                      style={[
                        styles.bodyRow,
                        isDark && { backgroundColor: '#1E1E1E', borderBottomColor: '#303030' },
                        rowIndex % 2 === 1 && styles.bodyRowAlt,
                        rowIndex % 2 === 1 && isDark && { backgroundColor: '#242424' },
                      ]}
                    >
                      <TouchableOpacity
                        style={[
                          styles.cell,
                          styles.leftCell,
                          { width: LOAD_EXECUTION_COLUMN_WIDTHS.expand },
                        ]}
                        onPress={() => toggleExpand(rowKey, row.id, isExpandable)}
                      >
                        <View style={styles.expandLabelWrap}>
                          {isExpandable && (
                            <MaterialIcons
                              name={
                                isExpanded ? "keyboard-arrow-down" : "chevron-right"
                              }
                              size={16}
                              color={isDark ? "#9A9A9A" : "#6B7280"}
                            />
                          )}
                        </View>
                      </TouchableOpacity>
                      <View
                        style={[
                          styles.cell,
                          styles.leftCell,
                          { width: LOAD_EXECUTION_COLUMN_WIDTHS.id },
                        ]}
                      >
                        <Text style={[styles.linkText, isDark && { color: '#3D8BFF' }]}>{row.id}</Text>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.deliveryDate }]}>
                        <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>{row.deliveryDate}</Text>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.location }]}>
                        <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>{row.locationName || row.location}</Text>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.localRouteId }]}>
                        <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
                          {formatLocalRouteWithSequence(row.localRouteId, row.sequence)}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.nationalRouteId }]}>
                        <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
                          {row.nationalRouteId}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.driver }]}>
                        {isBlankDriver ? (
                          <View style={[styles.tag, styles.tagNeutral]}>
                            <Text style={styles.tagText}>-</Text>
                          </View>
                        ) : (
                          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>{row.driver}</Text>
                        )}
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.manifestArrivalDateTime }]}>
                        {isBlankManifestArrivalDateTime ? (
                          <View style={[styles.tag, styles.tagNeutral]}>
                            <Text style={styles.tagText}>-</Text>
                          </View>
                        ) : (
                          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
                            {row.manifestArrivalDateTime}
                          </Text>
                        )}
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualArrivalDateTime }]}>
                        {isBlankActualArrivalDateTime ? (
                          <View style={[styles.tag, styles.tagNeutral]}>
                            <Text style={styles.tagText}>-</Text>
                          </View>
                        ) : (
                          <Text style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}>
                            {row.actualArrivalDateTime}
                          </Text>
                        )}
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.arrivedAsExpected }]}>
                        {row.arrivedAsExpected === "Blank" ? (
                          <View style={[styles.tag, styles.tagNeutral]}>
                            <Text style={styles.tagText}>-</Text>
                          </View>
                        ) : row.arrivedAsExpected === "Y" ? (
                          <View style={[styles.tag, styles.tagArrivedYes]}>
                            <Text style={styles.tagArrivedYesText}>
                              {row.arrivedAsExpected}
                            </Text>
                          </View>
                        ) : (
                          <View style={[styles.tag, styles.tagRed]}>
                            <Text style={styles.tagText}>
                              {row.arrivedAsExpected}
                            </Text>
                          </View>
                        )}
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.actualCompletedDateTime }]}>
                        <View style={styles.completionTimeWrap}>
                          <View
                            testID={row.highlightCompletionTime ? `load-execution-completion-time-${row.id}` : undefined}
                            style={[
                              row.highlightCompletionTime && styles.completionTimeHighlight,
                              row.highlightCompletionTime && row.completionTimeHighlightColor
                                ? { backgroundColor: row.completionTimeHighlightColor }
                                : null,
                              row.highlightCompletionTime && isDark && styles.completionTimeHighlightDark,
                            ]}
                          >
                            <Text
                              style={[
                                styles.bodyText,
                                isDark && { color: '#D0D0D0' },
                                row.highlightCompletionTime && isDark && styles.completionTimeHighlightDarkText,
                              ]}
                            >
                              {row.actualCompletedDateTime || "-"}
                            </Text>
                          </View>
                          {row.highlightCompletionTime && row.completionTimeTooltip ? (
                            <DLOTooltip
                              content={row.completionTimeTooltip}
                              position="top"
                              trigger={summaryTooltipTrigger}
                              maxWidth={280}
                            >
                              <View accessibilityLabel="Completion time information">
                                <MaterialIcons
                                  name="info-outline"
                                  size={15}
                                  color={isDark ? '#9A9A9A' : '#667085'}
                                />
                              </View>
                            </DLOTooltip>
                          ) : null}
                        </View>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.palletCount }]}>
                        <Text
                          testID={`load-execution-pallet-count-${row.id}`}
                          style={[styles.bodyText, isDark && { color: '#D0D0D0' }]}
                        >
                          {row.palletCount == null ? "-" : row.palletCount}
                        </Text>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.percentComplete }]}>
                        <View style={styles.barWrap}>
                          <View style={[styles.barTrack, isDark && { backgroundColor: '#3A3A3A' }]}>
                            <View
                              style={[
                                styles.barFill,
                                row.percentComplete < 100 &&
                                  styles.barFillNegative,
                                {
                                  width: `${Math.max(
                                    0,
                                    Math.min(100, row.percentComplete)
                                  )}%`,
                                },
                              ]}
                            />
                          </View>
                          <Text
                            style={[
                              styles.bodyText,
                              row.percentComplete >= 100
                                ? styles.onTimePositive
                                : styles.onTimeNegative,
                            ]}
                          >
                            {row.percentComplete.toFixed(0)}%
                          </Text>
                        </View>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.onTime }]}>
                        <View style={[styles.tag, onTimeTagStyle]}>
                          <Text style={onTimeTagTextStyle}>
                            {isBlankOnTime
                              ? "-"
                              : row.onTime
                              ? "On Time"
                              : "Late"}
                          </Text>
                        </View>
                      </View>
                      <View style={[styles.cell, { width: LOAD_EXECUTION_COLUMN_WIDTHS.status, paddingRight: 2 }]}>
                        <View style={[styles.tag, tagStyle]}>
                          <Text style={tagTextStyle}>{row.status}</Text>
                        </View>
                      </View>
                    </View>

                    {isExpanded && renderSsccPanelContent(row, rowKey)}
                  </View>
                );
              })}
            </View>
              </ScrollView>
            </View>
          )}
        </View>

        <View style={styles.footerRow}>
            <Text style={[styles.footerText, isDark && { color: '#9A9A9A' }]}>
              {`Showing ${currentStart}-${currentEnd} of ${totalCount.toLocaleString()} loads`}
            </Text>
            <View style={styles.paginationControls}>
              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                  currentPage <= 1 && styles.paginationButtonDisabled,
                  currentPage <= 1 && isDark && { backgroundColor: '#1E1E1E', borderColor: '#303030' },
                ]}
                disabled={currentPage <= 1}
                onPress={handlePrevPage}
                accessibilityLabel="Previous page"
              >
                <MaterialIcons
                  name="chevron-left"
                  size={20}
                  color={currentPage <= 1 ? (isDark ? '#4B4B4B' : '#CED4DA') : (isDark ? '#3D8BFF' : '#0B5FC6')}
                />
              </TouchableOpacity>
              <Text style={[styles.paginationPageText, isDark && { color: '#D0D0D0' }]}>
                {`Page ${Math.min(currentPage, totalPages)} of ${totalPages}`}
              </Text>
              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                  currentPage >= totalPages && styles.paginationButtonDisabled,
                  currentPage >= totalPages && isDark && { backgroundColor: '#1E1E1E', borderColor: '#303030' },
                ]}
                disabled={currentPage >= totalPages}
                onPress={handleNextPage}
                accessibilityLabel="Next page"
              >
                <MaterialIcons
                  name="chevron-right"
                  size={20}
                  color={currentPage >= totalPages ? (isDark ? '#4B4B4B' : '#CED4DA') : (isDark ? '#3D8BFF' : '#0B5FC6')}
                />
              </TouchableOpacity>
            </View>
            <View style={styles.pageSizeWrap}>
              {([25, 50, 100] as const).map((size) => (
                <TouchableOpacity
                  key={size}
                  onPress={() => handlePageSizeSelect(size)}
                  style={[
                    styles.pageSizePill,
                    isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                    currentPageSize === size && styles.pageSizePillActive,
                    currentPageSize === size && isDark && { backgroundColor: '#1B2A3D', borderColor: '#3D8BFF' },
                  ]}
                >
                  <Text
                    style={[
                      styles.pageSizeText,
                      isDark && { color: '#9A9A9A' },
                      currentPageSize === size && styles.pageSizeTextActive,
                      currentPageSize === size && isDark && { color: '#3D8BFF' },
                    ]}
                  >
                    {size}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
        </View>
        )}
    </View>
    );
  };

const styles = StyleSheet.create({
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 14,
  },
  summaryCard: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 172,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 14,
    paddingVertical: 14,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  // Note: the selected-card appearance (Incomplete Loads applied) is no
  // longer a static StyleSheet entry here — it's computed inline at the
  // call sites directly from the global theme's `colors.cardSelected`
  // tokens (see useTheme()) so Light/Dark values can never drift apart.
  summaryHeaderRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 6,
  },
  summaryTitleGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  summaryLabel: {
    flex: 1,
    fontSize: 11,
    lineHeight: 14,
    color: "#667085",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  summaryInfoDot: {
    width: 16,
    height: 16,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#98A2B3",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  summaryInfoDotText: {
    fontSize: 10,
    lineHeight: 10,
    color: "#98A2B3",
    fontFamily: FontFamily.bold,
  },
  summaryValue: {
    fontSize: 32,
    lineHeight: 36,
    color: "#111827",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    marginBottom: 6,
  },
  summaryValuePositive: {
    color: "#005CBC",
  },
  summaryValueNegative: {
    color: "#C2410C",
  },
  summarySubText: {
    fontSize: 12,
    lineHeight: 16,
    color: "#6B7280",
    fontFamily: FontFamily.regular,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingTop: 14,
    paddingBottom: 8,
    paddingLeft: 16,
    paddingRight: 0,  // Children (headerRow, tableContainer) have explicit paddingRight: 10
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    overflow: "hidden",
  },
  headerRow: {
    paddingHorizontal: 0,
    paddingLeft: 0,
    paddingRight: 10,
    marginBottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  title: {
    fontSize: 18,
    color: "#111827",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
  },
  subtitle: {
    marginTop: 2,
    fontSize: 12,
    color: "#6B7280",
    fontFamily: FontFamily.regular,
  },
  exportBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#0B5FC6",
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
    alignSelf: "flex-start",
  },
  exportBtnText: {
    fontSize: 14,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
  },
  alertBand: {
    marginHorizontal: 16,
    marginBottom: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#FECACA",
  },
  alertBandText: {
    fontSize: 12,
    color: "#9F1239",
    fontFamily: FontFamily.medium,
  },
  outerContainer: {
    flex: 1,
    flexDirection: "column",
  },
  tableContainer: {
    width: "100%",
    overflow: "hidden",
  },
  tableWrap: {},
  // ✅ Sticky Dispatch ID column layout (iPad & mobile breakpoints). The
  // fixed left column sits outside the horizontally-scrolling ScrollView
  // as a true sibling View — this is what makes it "stick" on native iOS
  // and Android too, not just web (there is no native equivalent of CSS
  // `position: sticky` for horizontal scroll content in React Native).
  stickyTableRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
  },
  stickyLeftCol: {
    borderRightWidth: 1,
    borderRightColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    zIndex: 2,
    elevation: 3,
    shadowColor: "#101828",
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  stickyLeftColDark: {
    borderRightColor: "#404040",
    backgroundColor: "#1E1E1E",
    shadowColor: "#000000",
    shadowOpacity: 0.35,
  },
  stickyScrollArea: {
    flex: 1,
  },
  // The scrollable side's first column (Delivery Date) otherwise butts
  // directly against the sticky Dispatch ID column's right border/shadow
  // with zero breathing room — this adds a clear visual gap so its header
  // and body text never look like they're touching the sticky divider.
  stickyScrollContent: {
    paddingLeft: 14,
  },
  // Blank spacer rendered under the sticky Dispatch ID cell for an
  // expanded row, sized (via inline `height`) to match the measured
  // height of that row's SSCC panel on the scrollable side, so row
  // boundaries stay aligned between the fixed column and the scrolling
  // columns behind it.
  stickyPanelSpacer: {
    backgroundColor: "#FFFFFF",
    borderBottomWidth: 1,
    borderBottomColor: "#EEF0F4",
  },
  headerCellsRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderBottomWidth: 2,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    paddingLeft: 0,
    paddingRight: 0,
    minHeight: 42,
    alignItems: "center",
  },
  bodyRow: {
    flexDirection: "row",
    paddingLeft: 0,
    paddingRight: 0,
    minHeight: 44,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#EEF0F4",
    backgroundColor: "#FFFFFF",
  },
  bodyRowAlt: {
    backgroundColor: "#FCFDFF",
  },
  cell: {
    justifyContent: "center",
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 10,
  },
  leftCell: {
    alignItems: "flex-start",
  },
  headerCellText: {
    fontSize: 11,
    color: "#667085",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    lineHeight: 14,
  },
  headerCellTextActive: {
    color: "#0B5FC6",
  },
  sortHeaderContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  sortIcon: {
    marginLeft: 2,
  },
  bodyText: {
    fontSize: 13,
    color: "#111827",
    fontFamily: FontFamily.medium,
  },
  completionTimeWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
  },
  completionTimeHighlight: {
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  // Overrides the backend's light highlight colour, which is unreadable with light text on dark surfaces.
  completionTimeHighlightDark: {
    backgroundColor: "#4A3B0E",
    borderWidth: 1,
    borderColor: "#7A6218",
  },
  completionTimeHighlightDarkText: {
    color: "#FDE68A",
  },
  linkText: {
    fontSize: 13,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  expandLabelWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  tag: {
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    alignSelf: "flex-start",
  },
  tagGreen: {
    backgroundColor: "#E7F6ED",
  },
  tagInfo: {
    backgroundColor: "#E6F2FF",
  },
  tagRed: {
    backgroundColor: "#FEEDEC",
  },
  tagNeutral: {
    backgroundColor: "#EEF2F6",
  },
  tagText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: "#334155",
  },
  // ✅ NEW: Custom tag styles for specific columns
  tagArrivedYes: {
    backgroundColor: "#CAF3C6",
  },
  tagArrivedYesText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: "#0C420B",
  },
  tagCompleteCustom: {
    backgroundColor: "#DAEEFA",
  },
  tagCompleteCustomText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: "#01346F",
  },
  tagOnTimeCustom: {
    backgroundColor: "#CAF3C6",
  },
  tagOnTimeCustomText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: "#0C420B",
  },
  tagLateCustom: {
    backgroundColor: "#FFE5DE",
  },
  tagLateCustomText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: "#AB2B09",
  },
  onTimePositive: {
    color: "#005CBC",
    fontFamily: FontFamily.bold,
  },
  onTimeNegative: {
    color: "#DC2626",
    fontFamily: FontFamily.bold,
  },
  barWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  barTrack: {
    width: 50,
    maxWidth: 50,
    height: 8,
    borderRadius: 999,
    backgroundColor: "#E9EEF6",
    overflow: "hidden",
    flexShrink: 0,
  },
  barFill: {
    height: "100%",
    backgroundColor: "#0B5FC6",
    borderRadius: 999,
  },
  barFillNegative: {
    backgroundColor: "#DC2626",
  },
  ssccPanel: {
    paddingLeft: 50,
    paddingRight: 16,
    paddingTop: 10,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EEF0F4",
    backgroundColor: "#FFFFFF",
  },
  ssccHeaderRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
    paddingBottom: 6,
    marginBottom: 2,
    alignItems: "center",
  },
  ssccBodyRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#F2F4F7",
    paddingVertical: 6,
    alignItems: "center",
  },
  ssccHeaderCell: {
    fontSize: 11,
    color: "#667085",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },
  ssccBodyCell: {
    fontSize: 12,
    color: "#334155",
    fontFamily: FontFamily.medium,
  },
  // Loading / error-with-retry / "No data present" states for the SSCC
  // breakdown panel (lazily fetched on expand — see fetchSsccBreakdownForRow).
  ssccStateWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
  },
  ssccStateText: {
    fontSize: 13,
    color: "#6B7280",
    fontFamily: FontFamily.regular,
  },
  ssccEmptyText: {
    fontSize: 13,
    color: "#6B7280",
    fontFamily: FontFamily.medium,
  },
  ssccErrorText: {
    fontSize: 13,
    color: "#B42318",
    fontFamily: FontFamily.medium,
  },
  ssccRetryBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderWidth: 1,
    borderColor: "#0B5FC6",
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: "transparent",
  },
  ssccRetryBtnText: {
    fontSize: 12,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
  },
  emptyWrap: {
    paddingHorizontal: 24,
    paddingVertical: 36,
    alignItems: "center",
  },
  emptyTableState: {
    width: "100%",
    minHeight: 160,
    justifyContent: "center",
  },
  emptyTitle: {
    fontSize: 18,
    color: "#1F2937",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    marginBottom: 6,
  },
  emptyText: {
    fontSize: 13,
    color: "#6B7280",
    fontFamily: FontFamily.regular,
  },
  footerRow: {
    paddingHorizontal: 0,
    paddingLeft: 0,
    paddingRight: 10,  // Matches tableContainer right padding (10px constraint)
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  footerText: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: FontFamily.medium,
  },
  paginationControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  paginationButton: {
    width: 28,
    height: 28,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#DEE2E6",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  paginationButtonDisabled: {
    backgroundColor: "#F8F9FA",
    borderColor: "#F0F0F0",
  },
  paginationPageText: {
    fontSize: 12,
    color: "#111827",
    fontFamily: FontFamily.medium,
    minWidth: 90,
    textAlign: "center",
  },
  pageSizeWrap: {
    flexDirection: "row",
    gap: 6,
    alignItems: "center",
  },
  pageSizePill: {
    width: 30,
    height: 24,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  pageSizePillActive: {
    borderColor: "#0B5FC6",
    backgroundColor: "#EFF5FF",
  },
  pageSizeText: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: FontFamily.bold,
  },
  pageSizeTextActive: {
    color: "#0B5FC6",
  },
});

export default LoadExecution;